import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from './AuthContext';

const PurchaseContext = createContext();

/**
 * PurchaseContext now derives access from the authenticated user record.
 * - hasPurchased: user has access via stripe payment OR manual admin grant
 * - testerMode: user has access via tester-code redemption (temporary)
 * - hasAccess: either of the above
 * - Unauthenticated users have no access (only free Module 1).
 */
export function PurchaseProvider({ children }) {
  const { user, isAuthenticated, isLoading, claimStripe, refreshUser } = useAuth();

  const value = useMemo(() => {
    const accessSource = user?.access_source;
    const isTester = accessSource === 'tester';
    const hasPaidOrManual = !!user?.has_access && accessSource !== 'tester';
    const hasAccess = !!user?.has_access;

    return {
      hasPurchased: hasPaidOrManual,
      hasAccess,
      testerMode: {
        isActive: isTester,
        expiresAt: user?.access_expires_at || null,
      },
      isLoading,
      purchaseDate: user?.access_granted_at || null,
      user,
      isAuthenticated,
      claimStripe,
      refreshUser,
      getTesterExpiryDate: () => {
        if (!user?.access_expires_at) return null;
        const d = new Date(user.access_expires_at);
        return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
      },
    };
  }, [user, isAuthenticated, isLoading, claimStripe, refreshUser]);

  return <PurchaseContext.Provider value={value}>{children}</PurchaseContext.Provider>;
}

export function usePurchase() {
  const context = useContext(PurchaseContext);
  if (!context) throw new Error('usePurchase must be used within a PurchaseProvider');
  return context;
}
